import Dexie, { type EntityTable } from 'dexie';
import type { KioskSubmission, SyncStatus } from '@ecc/shared';

/**
 * Coda locale delle richieste (§9.2).
 *
 * Regola centrale: il visitatore non attende la rete. La richiesta viene
 * salvata qui, il successo viene mostrato, e la sincronizzazione avviene dopo.
 *
 * Il payload contiene dati personali, quindi resta sul dispositivo per il tempo
 * strettamente necessario: appena il server conferma, viene eliminato e della
 * riga sopravvivono solo metadati tecnici non personali (§9.4).
 */

export type QueuedSubmission = {
  /** UUID generato dal client: è anche la chiave di idempotenza lato server (§9.3). */
  localId: string;
  /** Payload minimizzato. `null` dopo la sincronizzazione: i dati non restano. */
  payload: KioskSubmission | null;
  createdAt: number;
  syncStatus: SyncStatus;
  syncAttempts: number;
  /** Messaggio tecnico dell'ultimo errore. Mai dati personali. */
  lastSyncError: string | null;
  syncedAt: number | null;
};

const database = new Dexie('flc-kiosk') as Dexie & {
  submissions: EntityTable<QueuedSubmission, 'localId'>;
};

database.version(1).stores({
  submissions: 'localId, syncStatus, createdAt',
});

export const queueDb = database;

/**
 * Soglia oltre la quale un record non sincronizzato va segnalato all'operatore.
 * §9.4 suggerisce 24 ore; il valore va confermato dal Titolare.
 */
export const STALE_PENDING_THRESHOLD_MS = 24 * 60 * 60 * 1000;

export class LocalPersistenceError extends Error {}

/**
 * Salva la richiesta in coda.
 *
 * Se questa operazione fallisce, il chiamante **non deve** mostrare il successo
 * al visitatore: significherebbe dirgli che la richiesta è stata registrata
 * quando non lo è (§26).
 */
export async function enqueue(submission: KioskSubmission): Promise<QueuedSubmission> {
  const record: QueuedSubmission = {
    localId: submission.submissionId,
    payload: submission,
    createdAt: Date.now(),
    syncStatus: 'PENDING',
    syncAttempts: 0,
    lastSyncError: null,
    syncedAt: null,
  };

  try {
    await queueDb.submissions.add(record);
  } catch (cause) {
    throw new LocalPersistenceError('Salvataggio locale non riuscito', { cause });
  }

  return record;
}

export async function listPending(): Promise<QueuedSubmission[]> {
  return queueDb.submissions.where('syncStatus').anyOf('PENDING', 'FAILED').toArray();
}

export async function markSyncing(localId: string): Promise<void> {
  await queueDb.submissions.update(localId, { syncStatus: 'SYNCING' });
}

/**
 * Conferma di avvenuta sincronizzazione.
 *
 * Elimina il payload personale e conserva la sola traccia tecnica, utile a
 * diagnosticare la coda senza custodire dati del visitatore (§9.4).
 */
export async function markSynced(localId: string): Promise<void> {
  await queueDb.submissions.update(localId, {
    payload: null,
    syncStatus: 'SYNCED',
    syncedAt: Date.now(),
    lastSyncError: null,
  });
}

export async function markFailed(localId: string, error: string): Promise<void> {
  const existing = await queueDb.submissions.get(localId);
  await queueDb.submissions.update(localId, {
    syncStatus: 'FAILED',
    syncAttempts: (existing?.syncAttempts ?? 0) + 1,
    // Messaggio tecnico troncato: non deve diventare un canale di fuga di dati.
    lastSyncError: error.slice(0, 200),
  });
}

/** Record sincronizzati e privi di payload: si possono rimuovere del tutto. */
export async function purgeSynced(): Promise<number> {
  return queueDb.submissions.where('syncStatus').equals('SYNCED').delete();
}

export async function countUnsynced(): Promise<number> {
  return queueDb.submissions.where('syncStatus').anyOf('PENDING', 'SYNCING', 'FAILED').count();
}

/**
 * Record fermi da troppo tempo.
 *
 * Servono ad avvisare l'operatore: dati personali che restano su un tablet
 * oltre il necessario sono un rischio, non un dettaglio operativo (§9.4).
 */
export async function listStalePending(now = Date.now()): Promise<QueuedSubmission[]> {
  const pending = await listPending();
  return pending.filter((record) => now - record.createdAt > STALE_PENDING_THRESHOLD_MS);
}
